import { DateFormatter, CustomButton } from '@/shared/ui/components';
import { DeviceIdFormatter, checkUsingHighSamplingRateDevice } from '@/entities/device';
import { PatientNameFormatter, StudyFidFormatter } from '@/entities/study';
import { markReturnedIcon, viewMoreIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { generateFullName, convertToMoment } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';

/**
 * Renders the device id of a waiting-to-return row as a clickable link that opens the device information drawer for that row. Marks the device as high sampling rate by checking the last sync schema of the linked study's device, and shows a skeleton until isLoadedData is true.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The device id formatter element, or a loading skeleton.
 */
const deviceIdFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, toggleDeviceInfoDrawer } = formatExtraData;
  if (isLoadedData) {
    return (
      <DeviceIdFormatter
        deviceId={cell}
        handleClickDeviceId={() => {
          toggleDeviceInfoDrawer(row);
        }}
        isHighSamplingRate={checkUsingHighSamplingRateDevice(row?.study?.device?.lastSync?.schema)}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the facility name of a waiting-to-return row as raw cell content with no decoration. Returns a single-line skeleton while the table's isLoadedData flag is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The facility cell value, or a loading skeleton.
 */
const facilityFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return cell;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the patient name of a waiting-to-return row as a clickable link that opens the patient drawer for that row. Builds the displayed name with generateFullName from the linked study's patient info, and shows a skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The patient name formatter element, or a loading skeleton.
 */
const patientNameFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    const { togglePatientDrawer } = formatExtraData;
    return (
      <PatientNameFormatter
        patientName={generateFullName(row?.study.info?.patient)}
        handleClickPatientName={() => {
          togglePatientDrawer(row);
        }}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the study start date of a waiting-to-return row through the shared date formatter,
 * localised to the linked study's timezone. Shows a single-line skeleton inside a status region
 * named "Loading study start date" while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The formatted study start date, or a loading skeleton.
 */
const studyStartDateFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return <DateFormatter date={cell} timezone={row.study?.timezone} />;
  }
  return (
    <span role="status" aria-label="Loading study start date">
      <Skeleton count={1} />
    </span>
  );
};

/**
 * Renders the shipped date of a waiting-to-return row through the shared date formatter, localised to the linked study's timezone. Shows a single-line skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The formatted shipped date, or a loading skeleton.
 */
const shippedDateFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return <DateFormatter date={cell} timezone={row.study?.timezone} />;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the study FID of a waiting-to-return row as a clickable link that opens the study drawer for that row. Delegates rendering to the shared study FID formatter and shows a skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The study FID formatter element, or a loading skeleton.
 */
const studyFidFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, toggleStudyDrawer } = formatExtraData;
  if (isLoadedData) {
    return (
      <StudyFidFormatter
        studyId={cell}
        handleClickStudyId={() => {
          toggleStudyDrawer(row);
        }}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the return tracking number of a waiting-to-return row as an external link to the carrier's tracking page from the last return label transaction. Emits a double-hyphen bullet when no tracking number exists and a skeleton while isLoadedData is false.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The tracking link, a placeholder, or a loading skeleton.
 */
const trackingFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    if (!cell) {
      return DOUBLE_HYPHEN_BULLET;
    }
    return (
      <a
        className="tracking-format"
        href={row?.lastReturnLabel?.transaction?.trackingUrlProvider}
        target="_blank"
        rel="noreferrer"
      >
        {cell}
      </a>
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Determines the date a device was returned for a waiting-to-return row, used to prefill the mark-as-returned modal. Takes the statusDate of the DELIVERED entry in the last return label's tracking history, or the current time when no delivered entry exists, and converts it to a moment in the study's timezone.
 *
 * @param row - The waiting-to-return device row to read the return date from.
 * @returns The return date as a moment in the study timezone, or null.
 */
const getReturnDate = (row: any) => {
  const deliveredDate = _.find(row.lastReturnLabel?.trackingHistory, {
    status: 'DELIVERED',
  })?.statusDate;
  const returnDate =
    convertToMoment({
      isoDate: deliveredDate || dayjs().toISOString(),
      timezone: row.study?.timezone,
    }) || null;
  return returnDate;
};

/**
 * Renders the two row actions of the waiting-to-return table: mark as returned and view details. The first opens the mark-device-returned modal with the row's id, deviceId and the return date derived from the last return label's delivered tracking entry, the second passes the row to onClickViewDetail, and nothing is rendered until isLoadedData is true.
 *
 * @param cell - The raw value of the cell being rendered.
 * @param row - The waiting-to-return device row the cell belongs to.
 * @param rowIndex - The zero-based index of the row in the table.
 * @param formatExtraData - Extra table context carrying isLoadedData and the drawer/modal togglers.
 * @returns The action buttons container, or null while data is still loading.
 */
const actionFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, onClickViewDetail, toggleMarkDeviceReturnedModal } = formatExtraData;
  if (isLoadedData) {
    return (
      <div className="action-formatter">
        <CustomButton
          id="mark-as-returned-btn"
          style="neutral"
          type="tertiary"
          size="small"
          icon={<ReactSVG src={markReturnedIcon} />}
          tooltipTitle={t`Mark as returned`}
          tooltipPlacement="bottom"
          ariaLabel={t`Mark as returned`}
          onClick={() => {
            toggleMarkDeviceReturnedModal([
              {
                id: row?.id,
                deviceId: row?.deviceId,
                returnDate: getReturnDate(row),
              },
            ]);
          }}
        />
        <CustomButton
          id="view-details-btn"
          style="neutral"
          type="tertiary"
          size="small"
          icon={<ReactSVG src={viewMoreIcon} />}
          tooltipTitle={t`View details`}
          tooltipPlacement="bottom"
          ariaLabel={t`View details`}
          onClick={() => {
            onClickViewDetail(row);
          }}
        />
      </div>
    );
  }
  return null;
};

export {
  actionFormatter,
  deviceIdFormatter,
  facilityFormatter,
  getReturnDate,
  patientNameFormatter,
  shippedDateFormatter,
  studyFidFormatter,
  studyStartDateFormatter,
  trackingFormatter,
};
